"use client";
import {NeoError} from "@/components/shared";
import {useRoutePlan} from "@/context/routePlan/optimizer/routePlanVariant";
import {useVariantMap} from "@/context/routePlan/optimizer/variantMap";
import {
  Image,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tooltip
} from "@nextui-org/react";
import {FaExchangeAlt} from "react-icons/fa";

import {useNeoContext} from "@/context/neo/neo.context";
import {CustomerType} from "@/libs/enums/live";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader
} from "@react-google-maps/api";
import {useEffect, useRef, useState} from "react";
import {createRoot} from "react-dom/client";
import {CreateBPBar} from "./createBP/createBp";
import {CreateInfowindow} from "./createBP/infowindow";
import {RoutesLeftBar} from "./leftSidebar";
import {BPInfoWindow} from "./leftWidgets/bpinfoWindow";
import {RoutesRightBar} from "./rightSidebar";
export const RoutesPlanMap = function () {
  const mapKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
  const mapRef = useRef<google.maps.Map | null>(null);
  const {
    routeGpsLocations,
    checkedRouteId,
    routeColors,
    refreshSingleRoute: reloadSingleroute,
    setCheckedRouteId,
    unMappedUsers,
    variantRoutesData
  } = useVariantMap();
  const {filterContext} = useNeoContext();

  const polylineRefs = useRef<{[routeId: number]: google.maps.Polyline}>({});
  const markerRefs = useRef<
    Record<
      number,
      {
        startMarker?: google.maps.Marker;
        endMarker?: google.maps.Marker;
        boardingMarkers?: google.maps.Marker[];
        infoWindows: any;
      }
    >
  >({});

  const {isLoaded, loadError} = useJsApiLoader({
    googleMapsApiKey: mapKey
  });
  const containerStyle = {
    width: "100%",
    height: "500px"
  };
  const mapStyles = [
    {
      featureType: "all",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#7c93a3"
        },
        {
          lightness: "-10"
        }
      ]
    },
    {
      featureType: "administrative.country",
      elementType: "geometry",
      stylers: [
        {
          visibility: "on"
        }
      ]
    },
    {
      featureType: "administrative.country",
      elementType: "geometry.stroke",
      stylers: [
        {
          color: "#c2d1d6"
        }
      ]
    },
    {
      featureType: "landscape",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#dde3e3"
        }
      ]
    },
    {
      featureType: "road.highway",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#c2d1d6"
        }
      ]
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [
        {
          color: "#a9b4b8"
        },
        {
          lightness: "0"
        }
      ]
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#a3c7df"
        }
      ]
    }
  ];
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(16);
  const [isHistoryTab, setIsHistoryTab] = useState(true);
  const {currentVariant, setCurrentVariant} = useRoutePlan();
  const [createMarkerPos, setCreateMarkerPos] =
    useState<google.maps.LatLngLiteral | null>(null);
  const [showCreateMarkerInfo, setCreateMarkerShowInfo] = useState(false);
  const [unmappedBpData, setUnmappedBpData] = useState<UnMappedLocation | null>(
    null
  );
  const createMarkerRef = useRef<google.maps.Marker | null>(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 11.0168,
    lng: 76.9558
  });
  const [selectedUnMamppedInfo, setSelectedUnMappedInfo] =
    useState<UnMappedLocation | null>(null);
  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map);
  };

  useEffect(() => {
    if (!mapRef.current) return;
    // Remove unchecked polylines
    Object.keys(polylineRefs.current).forEach(routeIdStr => {
      const routeId = Number(routeIdStr);
      if (!checkedRouteId.includes(routeId)) {
        polylineRefs.current[routeId].setMap(null); // remove from map
        delete polylineRefs.current[routeId];
      }
    });
    Object.keys(markerRefs.current).forEach(routeIdStr => {
      const routeId = Number(routeIdStr);
      if (!checkedRouteId.includes(routeId)) {
        // Remove start marker
        // if (markerRefs.current[routeId].startMarker) {
        //   markerRefs.current[routeId].startMarker!.setMap(null);
        // }
        // // Remove end marker
        // if (markerRefs.current[routeId].endMarker) {
        //   markerRefs.current[routeId].endMarker!.setMap(null);
        // }
        // Remove boarding markers
        if (markerRefs.current[routeId].boardingMarkers) {
          markerRefs.current[routeId].boardingMarkers!.forEach(marker => {
            marker.setMap(null);
          });
        }
        delete markerRefs.current[routeId];
      }
    });
    const bounds = new google.maps.LatLngBounds();

    // Add new polylines for checked routes
    checkedRouteId.forEach((routeId: number, index: number) => {
      if (!polylineRefs.current[routeId]) {
        applyPolylineAndMarkers(routeId, index, bounds);
      }
    });
    if (!bounds.isEmpty()) {
      mapRef.current?.fitBounds(bounds);
    }
  }, [checkedRouteId, routeGpsLocations]);

  useEffect(() => {
    if (!mapRef?.current) return;
    if (mapRef && unMappedUsers.length > 0) {
      const bounds = new google.maps.LatLngBounds();

      unMappedUsers.forEach((item: UnMappedLocation) => {
        bounds.extend({lat: item.latitude, lng: item.longitude});
      });

      mapRef?.current?.fitBounds(bounds);
    }
  }, [mapRef, unMappedUsers]);

  // useEffect(() => {
  //   if (!mapRef.current) return;

  //   // If marker does NOT exist → create it
  //   if (!createMarkerRef.current) {
  //     const marker = new google.maps.Marker({
  //       position: createMarkerPos ?? undefined,
  //       map: mapRef.current!,
  //       draggable: true,
  //       icon: {
  //         url: "/icons/routePlan/createMarker.svg",
  //         scaledSize: new google.maps.Size(35, 35),
  //         anchor: new google.maps.Point(20, 20)
  //       }
  //     });

  //     // Save marker
  //     createMarkerRef.current = marker;

  //     // InfoWindow setup
  //     // const container = document.createElement("div");
  //     // createRoot(container).render(
  //     //   <CreateInfowindow
  //     //     setCreateMarkerPos={setCreateMarkerPos}
  //     //     createMarkerPos={createMarkerPos}
  //     //   />
  //     // );

  //     // const infoWindow = new google.maps.InfoWindow({
  //     //   content: container
  //     // });

  //     // marker.addListener("click", () => {
  //     //   infoWindow.open({
  //     //     anchor: marker,
  //     //     map: mapRef.current!
  //     //   });
  //     // });

  //     // Drag event
  //     marker.addListener("dragend", (e: any) => {
  //       setCreateMarkerPos({
  //         lat: e.latLng?.lat() ?? 0,
  //         lng: e.latLng?.lng() ?? 0
  //       });
  //     });

  //     return;
  //   }

  //   // If marker already exists → only update position
  //   createMarkerRef.current.setPosition(createMarkerPos ?? undefined);
  // }, [createMarkerPos]);
  const applyPolylineAndMarkers = (
    routeId: number,
    index: number,
    bounds: any
  ) => {
    const route = routeGpsLocations[routeId];
    if (!route || !route.refData?.length) return;

    const path = route.refData.map((p: any) => ({
      lat: p.latitude,
      lng: p.longitude
    }));
    // Extend bounds for each point in the route
    path.forEach((p: any) => bounds.extend(p));
    polylineRefs.current[routeId] = new google.maps.Polyline({
      path,
      strokeColor: route.colorCode || routeColors?.[index] || "#999",
      strokeOpacity: 1.0,
      strokeWeight: 3,
      map: mapRef.current!
    });
    //Marker logic can be added here for userBoarding points
    if (route.userBoarding && route.userBoarding.length > 0) {
      markerRefs.current[routeId] = {
        boardingMarkers: [],
        infoWindows: []
      };
      route.userBoarding.forEach((boarding: any, i: number) => {
        //console.log("Adding boarding marker at:", boarding);
        const marker = new google.maps.Marker({
          position: {
            lat: boarding.latitude,
            lng: boarding.longitude
          },
          map: mapRef.current!,
          // icon: {
          //   url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
          //   scaledSize: new google.maps.Size(30, 30)
          // },
          label: {
            text:
              boarding.fencingId === -1
                ? "S"
                : boarding.fencingId === -2
                  ? "E"
                  : i.toString(),
            color: "#000",
            fontSize: "12px",
            fontWeight: "bold"
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: "#fff",
            fillOpacity: 1,
            strokeColor: route.colorCode,
            strokeWeight: 3,
            scale: 12 // controls circle size
          },
          title: `Boarding: ${boarding.name || ""}`
        });
        // Create a container div
        const infoWindowDiv = document.createElement("div");

        // Render your React component into that div
        createRoot(infoWindowDiv).render(
          <BPInfoWindow
            routeData={boarding}
            routeDetails={route.routeDetails}
            variantId={currentVariant}
            refreshSingleRoute={refreshSingleRoute}
            index={i}
            currentVariant={currentVariant}
            setCurrentVariant={setCurrentVariant}
            variantRoutesData={variantRoutesData}
          />
        );
        const infoWindow = new google.maps.InfoWindow({
          content: infoWindowDiv
        });

        marker.addListener("click", () => {
          infoWindow.open({
            anchor: marker,
            map: mapRef.current!
          });
        });
        markerRefs.current[routeId].boardingMarkers!.push(marker);
        markerRefs.current[routeId].infoWindows!.push(infoWindow);
      });
    }

    // End of Marker logic
  };
  const forceCheckUncheckRoute = async (routeId: number) => {
    setCheckedRouteId((prev: any) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.includes(routeId)
        ? list.filter((id: number) => id !== routeId)
        : [...list, routeId];
    });
  };
  const openSpecificInfoWindow = (routeId: number, index: number) => {
    const marker = markerRefs.current[routeId]?.boardingMarkers?.[index];
    const infoWindow = markerRefs.current[routeId]?.infoWindows?.[index];
    if (marker && infoWindow) {
      infoWindow.open({
        anchor: marker,
        map: mapRef.current!
      });
    }
  };

  const refreshSingleRoute = async (routeId: number, index = 0) => {
    forceCheckUncheckRoute(routeId); // Here route will be unchecked then old data removed
    await reloadSingleroute(routeId);
    forceCheckUncheckRoute(routeId); // Here route will be checked again to show updated data
    if (index > 0) {
      setTimeout(() => {
        openSpecificInfoWindow(routeId, index);
      }, 1000); // Delay to ensure markers are rendered
    }
  };

  // Create BP Logic below
  const handleCreateMarker = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      if (center) {
        setCreateMarkerPos({
          lat: center.lat(),
          lng: center.lng()
        });
        setCreateMarkerShowInfo(true);
      }
    }
  };

  const handleExchange = () => {
    // Implement exchange functionality here
    //console.log("Exchange clicked");
    //setModalBpEdit(true);
    setUnmappedBpData(null);
    setTimeout(() => setUnmappedBpData(selectedUnMamppedInfo), 1);
    setSelectedUnMappedInfo(null);
  };

  const handleDelete = () => {
    // Implement delete functionality here
    // console.log("Delete clicked");
    //DeletetBpwindow
    // setModalBpDelete(true);
  };
  return (
    <div className="flex min-h-[calc(100vh-63px)] w-full">
      <div className="relative h-[calc(100vh-123px)] w-full">
        {/* Google Map */}
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{width: "100%", height: "100%"}}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={onMapLoad}
            // clickableIcons={false}
            options={{
              styles: mapStyles,
              disableDefaultUI: true, // Disable all default controls
              zoomControl: true, // Optional
              mapTypeControl: false,
              mapTypeControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_LEFT, // move satellite control
                style: google.maps.MapTypeControlStyle.DEFAULT
              },
              fullscreenControl: true,
              fullscreenControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_RIGHT // move fullscreen button
              }
            }}
          >
            {/* Un Mapped markers Logic start */}
            {unMappedUsers.map((item: UnMappedLocation) => (
              <Marker
                key={item.id}
                position={{lat: item.latitude, lng: item.longitude}}
                onClick={() => {
                  setSelectedUnMappedInfo(null);
                  setTimeout(() => setSelectedUnMappedInfo(item), 1);
                }}
                icon={{
                  url: "/icons/routePlan/offRouteMarker.svg",
                  scaledSize: new google.maps.Size(35, 35),
                  anchor: new google.maps.Point(20, 20)
                }}
              />
            ))}

            {selectedUnMamppedInfo && (
              <InfoWindow
                position={{
                  lat: selectedUnMamppedInfo.latitude,
                  lng: selectedUnMamppedInfo.longitude
                }}
                onCloseClick={() => {
                  setSelectedUnMappedInfo(null);
                }}
              >
                <div className="rounded-xl bg-white p-2 shadow-sm ring-1 ring-black/5 md:p-2">
                  {/* Header */}
                  <div className="space-y-1">
                    <h2 className="text-sm font-semibold">Off Route BP</h2>
                    <p className="text-xs text-gray-500">
                      Manage route and user assignments
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Route Details */}
                    <section className="rounded-lg border border-gray-200 bg-gray-50">
                      <header className="flex items-center justify-between gap-4 px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-yellow-100 text-[11px]"> */}
                          {/* yellowroute */}
                          <Image
                            src={`/icons/routePlan/yellowroute.svg`}
                            alt={""}
                            width={20}
                            height={20}
                            // className="mb-4"
                          />
                          {/* </span> */}
                          <h3 className="text-sm font-semibold">BP Details</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <Tooltip
                            content="Exchange Route"
                            showArrow
                            color="primary"
                          >
                            <span
                              onClick={handleExchange}
                              className="inline-flex cursor-pointer items-center justify-center"
                            >
                              <FaExchangeAlt />
                            </span>
                          </Tooltip>
                          {/* <Tooltip
                            content="Delete Boarding Point"
                            showArrow
                            color="primary"
                          >
                            <span
                              onClick={handleDelete}
                              className="inline-flex cursor-pointer items-center justify-center"
                            >
                              <MdDelete />
                            </span>
                          </Tooltip> */}
                        </div>
                      </header>

                      <div className="space-y-3 px-4 pb-4">
                        <div className="rounded-lg border bg-white px-3 py-2.5">
                          <p className="text-[11px] font-medium text-gray-500">
                            BP Name
                          </p>
                          <p className="text-xs font-semibold text-gray-900">
                            {selectedUnMamppedInfo.name ?? "#"}
                          </p>
                        </div>

                        <div className="rounded-lg border bg-white px-3 py-2.5">
                          <p className="text-[11px] font-medium text-gray-500">
                            BP Location
                          </p>
                          <p className="flex items-center gap-2 text-xs font-semibold text-gray-900">
                            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full">
                              <Image
                                src={`/icons/routePlan/yellowroute.svg`}
                                alt={""}
                                width={20}
                                height={20}
                                // className="mb-4"
                              />
                            </span>
                            {selectedUnMamppedInfo.latitude.toFixed(4)},
                            {selectedUnMamppedInfo.longitude.toFixed(4)}
                          </p>
                        </div>
                      </div>
                    </section>
                    <section className="rounded-lg border border-gray-200 bg-gray-50">
                      <header className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-yellow-100 text-[11px]"> */}
                          {/* yellowroute */}
                          <Image
                            src={`/icons/studentCap.svg`}
                            alt={""}
                            width={20}
                            height={20}
                            // className="mb-4"
                          />
                          {/* </span> */}
                          <h3 className="text-sm font-semibold">
                            User Details
                          </h3>
                        </div>
                        {/* Will enable later */}
                        {/* <div className="flex items-center gap-2">
                                <Tooltip content="Map/Unmap User(s)" showArrow color="primary">
                                  <span
                                    onClick={handleBpEdit}
                                    className="inline-flex cursor-pointer items-center justify-center"
                                  >
                                    <MdEdit />
                                  </span>
                                </Tooltip>
                              </div> */}
                      </header>

                      <div className="px-4 pb-4">
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-[11px] font-medium text-gray-500">
                            Mapped Users
                          </p>
                          <span className="rounded-md bg-yellow-400 px-2 py-0.5 text-[10px] font-semibold text-black">
                            {selectedUnMamppedInfo?.users?.length ?? 0}
                          </span>
                        </div>

                        {selectedUnMamppedInfo?.users.length === 0 ? (
                          <p className="w-full rounded-md border bg-white p-2 text-xs text-gray-500 shadow-md">
                            No Users mapped
                          </p>
                        ) : (
                          <div className="max-h-28 overflow-y-auto pr-1">
                            <StudentList
                              unMappedUsers={selectedUnMamppedInfo}
                              filterContext={filterContext}
                            />
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              </InfoWindow>
            )}
            {unmappedBpData && (
              <InfoWindow
                position={{
                  lat: unmappedBpData.latitude,
                  lng: unmappedBpData.longitude
                }}
                onCloseClick={() => setUnmappedBpData(null)}
              >
                <CreateInfowindow
                  setCreateMarkerPos={setCreateMarkerPos}
                  createMarkerPos={createMarkerPos}
                  unmappedBpData={unmappedBpData}
                  setUnmappedBpData={setUnmappedBpData}
                />
              </InfoWindow>
            )}
            {/* Create Bp Marker Logic start */}
            {createMarkerPos && (
              <Marker
                position={createMarkerPos ?? undefined}
                draggable={true}
                onClick={() => {
                  // console.log("onclick");
                  // console.log(createMarkerPos);
                  setCreateMarkerShowInfo(false);
                  setTimeout(() => setCreateMarkerShowInfo(true));
                }}
                icon={{
                  url: "/icons/routePlan/createMarker.svg",
                  scaledSize: new google.maps.Size(35, 35),
                  anchor: new google.maps.Point(20, 20)
                }}
                onDragEnd={e => {
                  setCreateMarkerPos({
                    lat: e.latLng?.lat() ?? 0,
                    lng: e.latLng?.lng() ?? 0
                  });
                }}
              />
            )}

            {showCreateMarkerInfo && (
              //InfoWindow
              //OverlayView
              <InfoWindow
                position={createMarkerPos ? createMarkerPos : undefined}
                onCloseClick={() => {
                  setCreateMarkerShowInfo(false);
                  setCreateMarkerPos(null);
                }}
                //mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                {/* <div className="relative -translate-x-1/2  -translate-y-full"> */}
                <CreateInfowindow
                  setCreateMarkerPos={setCreateMarkerPos}
                  createMarkerPos={createMarkerPos}
                />
                {/* </div> */}
              </InfoWindow>
            )}
            {/* Create Bp Marker Logic end */}
          </GoogleMap>
        ) : loadError ? (
          <NeoError message={loadError.message} />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        )}

        {/* Left Sidebar */}
        <RoutesLeftBar
          setIsHistoryTab={setIsHistoryTab}
          isHistoryTab={isHistoryTab}
        />

        <CreateBPBar
          isDisabled={!variantRoutesData}
          createMarker={handleCreateMarker}
        />

        {/* {isHistoryTab && <RoutesRightBar />} */}
        <RoutesRightBar />
        {/* Right Sidebar */}
      </div>
    </div>
  );
};

export default function StudentList({
  unMappedUsers,
  filterContext
}: {
  unMappedUsers: {users: UserBoardingUser_RP[]};
  filterContext: any;
}) {
  return (
    <div className="grid grid-cols-1 gap-2">
      {unMappedUsers?.users.map(s => (
        <Popover key={s.admissionNo} placement="top" showArrow offset={10}>
          <PopoverTrigger>
            <div className="flex cursor-pointer items-center gap-2 rounded-md border bg-white px-3 py-2 transition hover:bg-gray-50">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm">
                <Image
                  src="/icons/stuUser.svg"
                  alt="user icon"
                  width={10}
                  height={10}
                />
              </span>
              <span className="truncate text-xs font-medium text-gray-700">
                {s.name}
              </span>
            </div>
          </PopoverTrigger>

          <PopoverContent className="p-3 text-sm">
            <div className="flex flex-col gap-1 text-gray-700">
              <div>
                <strong>Name:</strong> {s.name}
              </div>
              <div>
                <strong>
                  {" "}
                  {filterContext?.client?.customerType ===
                  CustomerType.EMPLOYEETRACKING
                    ? "Department"
                    : "Class"}
                  :
                </strong>{" "}
                {s.className}
              </div>
              <div>
                <strong>
                  {" "}
                  {filterContext?.client?.customerType ===
                  CustomerType.EMPLOYEETRACKING
                    ? "Branch"
                    : "Section"}
                  :
                </strong>{" "}
                {s.section}
              </div>
              <div>
                <strong>
                  {filterContext?.client?.customerType ===
                  CustomerType.EMPLOYEETRACKING
                    ? "Employee ID"
                    : "Admission No"}
                  :
                </strong>{" "}
                {s.admissionNo}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ))}
      {/* {routeData?.users.map((s: UserBoardingUser_RP) => (
                  <div
                    key={s.admissionNo}
                    className="flex items-center gap-2 rounded-md border bg-white px-3 py-2"
                  >
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm">
                      <Image
                        src={`/icons/stuUser.svg`}
                        alt={""}
                        width={10}
                        height={10}
                        // className="mb-4"
                      />
                    </span>

                    <span className="truncate text-xs font-medium text-gray-700">
                      {s.name}
                    </span>
                  </div>
                ))} */}
    </div>
  );
}
